<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
  isInTopbar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changeFilter']);

// Anchor the panel leftward whenever the trigger sits near the right edge of the
// window — expanded layout, or hoisted into the global topbar. The pills' lists open
// below each pill, inside the panel. On the phone there is no anchor: the ⇅ sits in
// the middle of the topbar, and the panel opens across the screen under it (max-sm).
const anchorRight = computed(
  () => props.isOnExpandedLayout || props.isInTopbar
);

const store = useStore();
const { t } = useI18n();

const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const [showActionsDropdown, toggleDropdown] = useToggle();

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.ALL;
});

const currentSortBy = computed(() => {
  return (
    chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const { STATUS_TYPE, SORT_BY_TYPE } = wootConstants;

// No formato da pílula: { id, name }. Os ids são os valores de antes, que o store, o
// ui_settings e a ChatList recebem.
const chatStatusOptions = computed(() => [
  {
    name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    id: STATUS_TYPE.OPEN,
  },
  {
    name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    id: STATUS_TYPE.RESOLVED,
  },
  {
    name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    id: STATUS_TYPE.PENDING,
  },
  {
    name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    id: STATUS_TYPE.SNOOZED,
  },
  {
    name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    id: STATUS_TYPE.ALL,
  },
]);

// Os ids saem de SORT_BY_TYPE, e só dele: o getter da lista também ordena no navegador
// (sortComparator), e um valor que ele não conhece cai calado na última atividade.
const chatSortOptions = computed(() => [
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_asc.TEXT'),
    id: SORT_BY_TYPE.LAST_ACTIVITY_AT_ASC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_desc.TEXT'),
    id: SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_desc.TEXT'),
    id: SORT_BY_TYPE.CREATED_AT_DESC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_asc.TEXT'),
    id: SORT_BY_TYPE.CREATED_AT_ASC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc.TEXT'),
    id: SORT_BY_TYPE.PRIORITY_DESC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_asc.TEXT'),
    id: SORT_BY_TYPE.PRIORITY_ASC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc_created_at_asc.TEXT'),
    id: SORT_BY_TYPE.PRIORITY_DESC_CREATED_AT_ASC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_asc.TEXT'),
    id: SORT_BY_TYPE.WAITING_SINCE_ASC,
  },
  {
    name: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_desc.TEXT'),
    id: SORT_BY_TYPE.WAITING_SINCE_DESC,
  },
]);

const saveSelectedFilter = (type, value) => {
  updateUISettings({
    conversations_filter_by: {
      status: type === 'status' ? value : currentStatusFilter.value,
      order_by: type === 'sort' ? value : currentSortBy.value,
    },
  });
};

// A pílula fala em lista de um item (v-model: Array); o store, o ui_settings e a ChatList, em
// string. Sem ✕ (clearable=false), ela nunca devolve a lista vazia.
const handleStatusChange = ([value]) => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = ([value]) => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};
</script>

<template>
  <div class="relative flex">
    <NextButton
      v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      icon="i-lucide-arrow-up-down"
      slate
      faded
      xs
      @click="toggleDropdown()"
    />
    <div
      v-if="showActionsDropdown"
      v-on-click-outside="() => toggleDropdown()"
      class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-max min-w-80 rounded-xl p-4 absolute z-40 top-full max-sm:fixed max-sm:inset-x-4 max-sm:top-14 max-sm:w-auto max-sm:min-w-0"
      :class="{
        'sm:ltr:left-0 sm:rtl:right-0': !anchorRight,
        'sm:ltr:right-0 sm:rtl:left-0': anchorRight,
      }"
    >
      <!-- UI-FILTROS-01 (#7): status e ordenação são pílulas únicas sem ✕ (clearable=false):
           sempre há um escolhido. O nome ("Status:", "Order by:") fica só na pílula, como na
           ordenação de Contatos e no menu das Notificações. O painel cresce com a pílula mais
           larga (w-max): a pílula não encolhe, e "Ordenar por: <ordem>" passava da borda dos
           w-72. A ordem é uma lista fixa de 9, sem a busca que a pílula põe a partir de 7. As 9
           ocupam 388 px, e o max-h-96 da lista (384) a fazia rolar por 4 px, com barra no
           computador: ela vai até 400 (25rem), e nunca além do fim da janela, porque o <main> do
           Dashboard corta com overflow-hidden: a parte que rola começa 154 px abaixo do topo da
           janela (170 no celular) e a lista termina 9 px depois; os 12rem (192) deixam folga.
           Numa janela baixa (667x375) ela rola, em vez de esconder 4 das 9. A do status, pelo
           mesmo corte, fica no max-h-96 de sempre e para no fim da janela: começa 114 px abaixo
           do topo (130 no celular), e os 10rem (160) deixam folga. Nos min-w-56 da pílula, a
           lista da ordem cortava 5 dos 9 nomes em pt_BR; com min-w-72, só o mais longo. O painel
           tem min-w-80 para ela caber dentro dele: começa depois do p-4 e da borda, e
           17 + 288 < 320. -->
      <div class="flex flex-col items-start gap-2">
        <FilterPill
          :label="$t('CHAT_LIST.CHAT_SORT.STATUS')"
          :options="chatStatusOptions"
          :model-value="[currentStatusFilter]"
          single
          :clearable="false"
          list-height="max-h-[min(24rem,calc(100dvh-10rem))]"
          @update:model-value="handleStatusChange"
        />
        <FilterPill
          :label="$t('CHAT_LIST.CHAT_SORT.ORDER_BY')"
          :options="chatSortOptions"
          :model-value="[currentSortBy]"
          single
          :clearable="false"
          :search-from="Infinity"
          list-height="max-h-[min(25rem,calc(100dvh-12rem))]"
          list-width="min-w-72"
          @update:model-value="handleSortChange"
        />
      </div>
    </div>
  </div>
</template>
